<template>
  <v-col
    class="container common-table-container pa-0"
    :class="{
      fullScreenMobile: this.$vuetify.breakpoint.mdAndDown
    }"
  >
    <CommonDialog
      :dialog="dialogDeleteOne"
      :head-text="$t(`${this.resource}.delete-title`)"
      :content-text="$t(`${this.resource}.delete-dialog`)"
      :confirm-button="{
        text: $t(`${this.resource}.delete`),
        icon: 'mdi-trash-can-outline'
      }"
      :on-confirm="deleteItem"
      :on-cancel="setItemToDelete"
    ></CommonDialog>
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="list"
      item-key="id"
      :items-per-page="limit"
      hide-default-footer
      :custom-sort="sort ? sort : customSort"
      :sort-by="ordersName"
      :sort-desc="ordersIsDesc"
      :loading="loading"
      :loading-text="$t(`tables.loading`)"
      no-data-text=""
      :height="isEmpty ? undefined : '100%'"
      :fixed-header="true"
      dense
      class="data-table"
      :show-select="showSelect"
    >
      <template
        v-for="slot in slots"
        v-slot:[`item.${slot.name}`]="{ item, value }"
      >
        <template
          v-if="
            (!item.isNewItem && !isEditItem(item)) || !canGiveForm(item, slot)
          "
        >
          <component
            v-if="itemComponents[slot.type]"
            v-bind:is="itemComponents[slot.type]"
            v-bind:key="slot.name + item[slot.name]"
            :item="item"
            :slot-data="slot"
            :list="list"
          ></component>
          <template v-else>
            <span v-if="slot.form.type === 'checkbox'" v-bind:key="slot.name">
              {{ item[slot.form.name || slot.name] ? $t("Yes") : $t("No") }}
            </span>
            <span v-else :key="'else-' + slot.name">{{ item[slot.name] }}</span>
          </template>
          <template v-if="item[slot.name]">
            {{ item[slot.decorator || ""] || "" }}
          </template>
        </template>
        <template v-else-if="canGiveForm(item, slot)">
          <v-form
            v-if="slot.form.visible"
            v-bind:key="'form' + slot.name"
            :ref="setRef(slot, isEditItem(item))"
          >
            <v-text-field
              v-bind:key="slot.name"
              v-if="slot.form.type === 'text'"
              v-model="item[slot.form.name || slot.name]"
              :placeholder="$t(`${resource}.${slot.title || slot.name}`)"
              :rules="[
                ...(slot.form.rules || []),
                backEndValidation(slot.form.name || slot.name)
              ]"
              :type="slot.form.input_type || 'text'"
              :suffix="
                item[slot.decorator || ''] || slot.decoratorDefault || ''
              "
            ></v-text-field>
            <v-select
              v-bind:key="slot.name"
              v-if="slot.form.type === 'select'"
              clearable
              :placeholder="$t(`${resource}.${slot.title || slot.name}`)"
              v-model="item[slot.form.name || slot.name]"
              :items="slot.form.items"
              @input="slot.form.change"
              :rules="[
                ...(slot.form.rules || []),
                backEndValidation(slot.form.name || slot.name)
              ]"
            >
            </v-select>
            <v-autocomplete
              v-bind:key="slot.name"
              v-if="slot.form.type === 'autocomplete'"
              clearable
              :placeholder="$t(`${resource}.${slot.title || slot.name}`)"
              v-model="item[slot.form.name || slot.name]"
              :items="slot.form.items"
              @input="slot.form.change"
              :rules="[
                ...(slot.form.rules || []),
                backEndValidation(slot.form.name || slot.name)
              ]"
            >
            </v-autocomplete>
            <v-combobox
              v-bind:key="slot.name"
              v-if="slot.form.type === 'combobox'"
              clearable
              :placeholder="$t(`${resource}.${slot.title || slot.name}`)"
              v-model="item[slot.form.name || slot.name]"
              :items="slot.form.items"
              :rules="[
                ...(slot.form.rules || []),
                backEndValidation(slot.form.name || slot.name)
              ]"
              @input="slot.form.change"
            >
            </v-combobox>
            <DateInput
              v-bind:key="slot.name"
              v-if="slot.form.type === 'date'"
              :date="item[slot.form.name || slot.name]"
              @update="
                val => {
                  updateDate(item, slot, val);
                }
              "
              :placeholder="$t(`${resource}.${slot.title || slot.name}`)"
              :icon="false"
              :rules="[
                ...(slot.form.rules || []),
                backEndValidation(slot.form.name || slot.name)
              ]"
              :loading="loading"
              hide-loading
            ></DateInput>
            <DateTimeInput
              class="pt-3"
              v-bind:key="slot.name"
              v-if="slot.form.type === 'date-time'"
              :date="item[slot.form.name || slot.name]"
              @update="val => $set(item, slot.form.name || slot.name, val)"
              :label="$t(`${resource}.${slot.title || slot.name}`)"
              :icon="false"
              :rules="[
                ...(slot.form.rules || []),
                backEndValidation(slot.form.name || slot.name)
              ]"
            ></DateTimeInput>
            <TimeInput
              class="pt-3"
              v-bind:key="slot.name"
              v-if="slot.form.type === 'time'"
              :date="item[slot.form.name || slot.name]"
              @update="val => $set(item, slot.form.name || slot.name, val)"
              :label="$t(`${resource}.${slot.title || slot.name}`)"
              :icon="false"
              :rules="[
                ...(slot.form.rules || []),
                backEndValidation(slot.form.name || slot.name)
              ]"
            ></TimeInput>
            <v-checkbox
              class="mt-2"
              v-bind:key="slot.name"
              v-if="slot.form.type === 'checkbox'"
              v-model="item[slot.form.name || slot.name]"
              color="primary"
              :rules="[
                ...(slot.form.rules || []),
                backEndValidation(slot.form.name || slot.name)
              ]"
            ></v-checkbox>
            <AutocompleteContacts
              v-if="slot.form.type === 'contact'"
              v-bind:key="slot.name"
              :item="item[slot.form.name || slot.name]"
              :updated="
                val => {
                  $set(item, slot.form.name || slot.name, val);
                }
              "
              :rules="[
                ...(slot.form.rules || []),
                backEndValidation(slot.form.name || slot.name)
              ]"
            ></AutocompleteContacts>
            <AutoCompleteTariffable
              v-if="slot.form.type === 'tariffable'"
              v-bind:key="slot.name"
              :item="item[slot.form.name || slot.name]"
              :updated="
                val => {
                  $set(item, slot.form.name || slot.name, val);
                }
              "
              :rules="[
                ...(slot.form.rules || []),
                backEndValidation(slot.form.name || slot.name)
              ]"
            ></AutoCompleteTariffable>
            <AutocompleteWithGroups
              v-if="slot.form.type === 'auto-group'"
              v-bind:key="slot.name"
              :item="item[slot.form.name || slot.name]"
              :placeholder="$t(`${resource}.${slot.title || slot.name}`)"
              :updated="
                val => {
                  $set(item, slot.form.name || slot.name, val);
                }
              "
              :rules="[
                ...(slot.form.rules || []),
                backEndValidation(slot.form.name || slot.name)
              ]"
              :items="slot.form.items"
            ></AutocompleteWithGroups>
            <CalculationBaseFormItem
              :item="item"
              :slot-data="slot"
              v-if="slot.form.type === 'calculation-base'"
            ></CalculationBaseFormItem>
            <FileFormItem
              :item="item"
              :slot-data="slot"
              :resource="resource"
              v-if="slot.form.type === 'file-input'"
            ></FileFormItem>
            <SourceFilterStringFormItem
              :item="item"
              :slot-data="slot"
              :resource="resource"
              v-if="slot.form.type === 'value-filter-string'"
              :valueFilter="slot.form.valueFilter"
            >
            </SourceFilterStringFormItem>
            <TreeSelector
              v-bind:key="slot.name"
              v-if="slot.form.type === 'tree-selector'"
              :placeholder="$t(`${resource}.${slot.title || slot.name}`)"
              v-model="item[slot.form.name || slot.name]"
              :items="
                slot.form.items.filter(
                  slot.form.filterItems
                    ? slot.form.filterItems(item)
                    : () => true
                )
              "
              :rules="[
                ...(slot.form.rules || []),
                backEndValidation(slot.form.name || slot.name)
              ]"
              :options-for-tree-items="slot.form.options"
            ></TreeSelector>
            <v-btn-toggle
              v-model="item[slot.form.name || slot.name]"
              color="#db2828"
              multiple
              v-if="slot.form.type === 'toggle'"
            >
              <v-btn
                v-for="(day, index) in slot.form.items"
                :key="index"
                :value="day.name"
                class="day-button"
              >
                <span>{{ day.label }}</span>
              </v-btn>
            </v-btn-toggle>
          </v-form>
        </template>
      </template>
      <template v-slot:item.action="{ item }">
        <div
          class="actions-container"
          :style="{ width: `${Math.max(actionsReal.length, 2) * 2}em` }"
        >
          <template v-if="item.isNewItem">
            <v-btn
              class="ml-2 mb-2 mobile"
              color="primary"
              :loading="loading"
              v-on:click="createItem(item)"
            >
              <v-icon>
                mdi-plus
              </v-icon>
              {{ $t(`buttons.create`) }}
            </v-btn>
            <v-icon
              color="primary"
              small
              class="mr-2 desktop"
              v-on:click="createItem(item)"
            >
              mdi-plus
            </v-icon>
          </template>
          <template v-else-if="item.id === editItem.id">
            <v-btn
              class="ml-2 mb-2 mobile"
              color="primary"
              :loading="loading"
              @click="edit"
            >
              <v-icon>
                mdi-check
              </v-icon>
              {{ $t(`buttons.edit`) }}
            </v-btn>
            <v-icon color="primary" small class="mr-2 desktop" @click="edit">
              mdi-check
            </v-icon>
            <v-btn
              class="ml-2 mb-2 mobile"
              color="primary"
              :loading="loading"
              @click="setItemToEdit()"
            >
              <v-icon>
                mdi-close
              </v-icon>
              {{ $t(`buttons.close`) }}
            </v-btn>
            <v-icon
              color="primary"
              small
              class="mr-2 desktop"
              @click="setItemToEdit()"
            >
              mdi-close
            </v-icon>
          </template>
          <template v-else>
            <template v-for="action in actionsReal">
              <template
                v-if="
                  hasPermissions(item, action._permissions) &&
                    (action.showFunction ? action.showFunction(item) : true)
                "
              >
                <v-btn
                  class="ml-2 mb-2 mobile"
                  color="primary"
                  v-if="action.type === 'edit'"
                  v-bind:key="item.id + action.type + 'mobile'"
                  @click="() => onClickAction(action, item)"
                >
                  <v-icon>
                    {{ action.icon || "mdi-pencil" }}
                  </v-icon>
                  {{ $t(`buttons.edit`) }}
                </v-btn>
                <v-icon
                  color="primary"
                  v-if="action.type === 'edit'"
                  v-bind:key="item.id + action.type"
                  @click="() => onClickAction(action, item)"
                  small
                  class="mr-2 desktop"
                >
                  {{ action.icon || "mdi-pencil" }}
                </v-icon>

                <v-btn
                  class="ml-2 mb-2 mobile"
                  v-if="action.type === 'editRow'"
                  @click="setItemToEdit(item)"
                  color="primary"
                  v-bind:key="item.id + action.type + 'mobile'"
                >
                  <span class="edit-row-icons">
                    <v-icon small>{{
                      action.icon_before || "mdi-pencil"
                    }}</v-icon>
                    <v-icon small class="second">{{
                      action.icon_after || "mdi-alpha-r"
                    }}</v-icon>
                  </span>
                  {{ $t(`buttons.edit`) }}
                </v-btn>
                <span
                  v-if="action.type === 'editRow'"
                  @click="setItemToEdit(item)"
                  class="edit-row-icons desktop"
                  v-bind:key="item.id + action.type"
                >
                  <v-icon color="primary" small>{{
                    action.icon_before || "mdi-pencil"
                  }}</v-icon>
                  <v-icon color="primary" small class="second">{{
                    action.icon_after || "mdi-alpha-r"
                  }}</v-icon>
                </span>

                <v-btn
                  class="ml-2 mb-2 mobile"
                  color="primary"
                  v-if="action.type === 'delete'"
                  v-bind:key="item.id + action.type + 'mobile'"
                  @click="!item.isWillRemoveSoon && setItemToDelete(item)"
                >
                  <v-icon>
                    {{ action.icon || "mdi-trash-can-outline" }}
                  </v-icon>
                  {{ $t(`buttons.delete`) }}
                </v-btn>
                <v-icon
                  v-if="action.type === 'delete'"
                  v-bind:key="item.id + action.type"
                  small
                  @click="!item.isWillRemoveSoon && setItemToDelete(item)"
                  class="mr-2 desktop"
                  color="primary"
                >
                  {{ action.icon || "mdi-trash-can-outline" }}
                </v-icon>

                <v-btn
                  class="ml-2 mb-2 mobile"
                  color="primary"
                  v-if="action.type === 'icon'"
                  v-bind:key="item.id + action.type + 'mobile'"
                  @click="() => onClickAction(action, item)"
                >
                  <v-icon>
                    {{ action.icon || "mdi-trash-can-outline" }}
                  </v-icon>
                  {{ action.text && $t(`buttons.${action.text}`) }}
                </v-btn>
                <v-icon
                  color="primary"
                  v-if="action.type === 'icon'"
                  v-bind:key="item.id + action.type"
                  small
                  @click="() => onClickAction(action, item)"
                  class="mr-2 desktop"
                >
                  {{ action.icon || " mdi-trash-can-outline" }}
                </v-icon>
              </template>
            </template>
          </template>
        </div>
      </template>
      <template v-slot:footer>
        <TableEmptyFooter
          :empty-text="emptyText"
          :can-add="canAdd"
          v-if="isEmpty && defaultEmptyFooter"
        ></TableEmptyFooter>
      </template>
    </v-data-table>
    <span v-html="style"></span>
  </v-col>
</template>

<script>
import { permissions } from "../../mixins/permissions";
import { rules } from "../../mixins/rules";
import CommonDialog from "../dialogs/CommonDialog";
import DateInput from "../inputs/DateInput";
import DateTimeInput from "../inputs/DateTimeInput.vue";
import TimeInput from "../inputs/TimeInput.vue";
import AutocompleteContacts from "../inputs/AutocompleteContacts";
import TableEmptyFooter from "./TableEmptyFooter";
import TreeSelector from "../inputs/TreeSelector";
//items
import LinkItem from "./items/LinkItem";
import RelatedLinkItem from "./items/RelatedLinkItem";
import DateItem from "./items/DateItem";
import AmountItem from "./items/AmountItem";
import NumberItem from "./items/NumberItem";
import StatusItem from "./items/StatusItem";
import GroupsItem from "./items/GroupsItem";
import DocumentImageItem from "./items/DocumentImageItem";
import RecordsModifiedItem from "./items/RecordsModifiedItem";
import ProjectsModulePriceItem from "./items/ProjectsModulePriceItem";
//form items
import CalculationBaseFormItem from "./form-items/CalculationBaseFormItem";
import FileFormItem from "./form-items/FileFormItem";
import SourceFilterStringFormItem from "./form-items/SourceFilterStringFormItem";
import AutocompleteWithGroups from "../inputs/AutocompleteWithGroups.vue";
import AutoCompleteTariffable from "../inputs/AutoCompleteTariffable.vue";

export default {
  name: "CommonTable",
  mixins: [permissions, rules],
  components: {
    CommonDialog,
    DateInput,
    DateTimeInput,
    AutocompleteContacts,
    LinkItem,
    RelatedLinkItem,
    DateItem,
    AmountItem,
    NumberItem,
    StatusItem,
    GroupsItem,
    DocumentImageItem,
    CalculationBaseFormItem,
    FileFormItem,
    TableEmptyFooter,
    SourceFilterStringFormItem,
    RecordsModifiedItem,
    ProjectsModulePriceItem,
    TreeSelector,
    TimeInput,
    AutocompleteWithGroups,
    AutoCompleteTariffable
  },
  props: {
    defaultEmptyFooter: {
      type: Boolean,
      default: true
    },
    listGetter: {
      type: String
    },
    listArray: {
      type: Array
    },
    headers: {
      type: Array,
      default: function() {
        return [];
      }
    },
    slots: {
      type: Array,
      default: function() {
        return [];
      }
    },
    actions: {
      type: Array,
      default: function() {
        return [];
      }
    },
    defaultLimit: {
      type: Number,
      default: 25
    },
    deleteItemDispatch: {
      type: String
    },
    updateItemDispatch: {
      type: String
    },
    createItemDispatch: {
      type: String
    },
    resource: {
      type: String
    },
    loading: {
      type: Boolean
    },
    canAdd: {
      type: Boolean,
      default: false
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    canDelete: {
      type: Boolean,
      default: false
    },
    staticDispatchParams: {
      type: Object,
      default: function() {
        return {};
      }
    },
    sort: {
      type: [Boolean, Function],
      default: function() {
        return false;
      }
    },
    emptyText: {
      type: String,
      default: "tables.empty-room"
    },
    showSelect: {
      type: Boolean,
      default: function() {
        return false;
      }
    },
    createItemCallback: {
      type: Function,
      default: function() {
        return () => {};
      }
    },
    updateItemCallback: {
      type: Function,
      default: function() {
        return () => {};
      }
    },
    deleteItemCallback: {
      type: Function,
      default: function() {
        return () => {};
      }
    },
    toggleState: {
      type: Boolean
    }
  },
  data: function() {
    return {
      selected: [],
      itemToDelete: null,
      dialogDeleteOne: false,
      dialogFilePreview: false,
      editItem: {},
      itemComponents: {
        link: "LinkItem",
        relatedLink: "RelatedLinkItem",
        date: "DateItem",
        amount: "AmountItem",
        number: "NumberItem",
        status: "StatusItem",
        groups: "GroupsItem",
        "document-image": "DocumentImageItem",
        "records-modified": "RecordsModifiedItem",
        "projects-module-price": "ProjectsModulePriceItem"
      },
      style: "",
      tariffPlanId: {}
    };
  },
  computed: {
    isEmpty() {
      return !(this.nativeList && this.nativeList.length) && !this.loading;
    },
    orders() {
      return this.$route.query.orders;
    },
    nativeList() {
      let list = [];
      if (this.listArray && this.listArray.length) {
        list = this.listArray;
      }
      if (this.listGetter) {
        list = this.$store.getters[this.listGetter];
      }
      return list;
    },
    list: {
      get() {
        let list = this.nativeList;

        if (this.canAdd) {
          list = [this.generateNewItem(), ...list];
        }
        if (this.editItem.id) {
          let index = list.findIndex(i => i.id === this.editItem.id);
          if (index) {
            list.splice(index, 1, this.editItem);
          }
        }
        return list;
      }
    },
    limit() {
      let limit = +this.$route.query.limit_items || +this.defaultLimit;
      return limit < 1 ? limit : limit + +this.canAdd;
    },
    ordersName() {
      if (this.orders) {
        return this.orders.split(":")[0];
      } else {
        return "";
      }
    },
    ordersIsDesc() {
      if (this.orders) {
        return this.orders.split(":")[1] === "desc";
      }
      return false;
    },
    actionsReal() {
      let actions = [...this.actions] || [];
      if (this.canEdit)
        actions.push({
          type: "editRow",
          icon_before: "mdi-pencil",
          icon_after: "mdi-alpha-r"
        });
      if (this.canDelete)
        actions.push({
          type: "delete",
          icon: "mdi-trash-can-outline"
        });
      return actions;
    }
  },
  watch: {
    list() {
      this.$nextTick(() => {
        this.resetItemsForm();
      });
    },
    headers: {
      handler: function() {
        this.formStyle();
      },
      deep: true
    },
    selected(val) {
      this.$emit("update:selected", val);
    },
    toggleState() {
      this.selected = [];
    }
  },
  methods: {
    generateNewItem() {
      let newItem = this.slots
        .filter(i => i.form)
        .reduce((ac, item) => {
          ac[item.form.name || item.name] = this.getNewItemInitialValue(item);
          return ac;
        }, {});
      newItem.isNewItem = true;
      return newItem;
    },
    getNewItemInitialValue(item) {
      if ("initialValue" in item) {
        return item.initialValue;
      } else if (item.form.type === "checkbox") {
        return false;
      } else if (item.form.type === "file-input") {
        return null;
      }
      return "";
    },
    customSort(items, index, isDesc) {
      let newRoute = {
        name: this.$route.name,
        query: { ...this.$route.query },
        params: { ...this.$route.params }
      };
      if (index && index[0]) {
        let newOrders = index[0] + ":" + (isDesc[0] ? "desc" : "asc");
        if (newOrders !== newRoute.query.orders)
          newRoute.query.orders = newOrders;
        this.$router.push(newRoute);
      } else {
        if (newRoute.query.orders) {
          delete newRoute.query.orders;
          this.$router.push(newRoute);
        }
      }
      return items;
    },
    setItemToDelete(item = null) {
      this.itemToDelete = item;
      this.dialogDeleteOne = !!item;
    },
    setItemToEdit(item = {}) {
      if (item.tariff_plan_id) {
        this.$store.dispatch("tariffPlanTimeframesStore/getList", {
          params: {
            filters: {
              tariff_plan_id: item.tariff_plan_id
            },
            project_id: item._project.id
          }
        });
        this.$store.dispatch("tariffPlanModifiersStore/getList", {
          params: {
            filters: {
              tariff_plan_id: item.tariff_plan_id
            },
            project_id: item._project.id
          }
        });
        this.$store.dispatch("tariffPlanComponentStore/getList", {
          params: {
            filters: {
              tariff_plan_id: item.tariff_plan_id
            },
            project_id: item._project.id
          }
        });
        setTimeout(() => {
          this.$root.$emit("updateTariffPlanId", item.tariff_plan_id);
        }, 500);
        this.editItem = { ...item };
        this.resetItemsForm("edit");
      } else {
        this.editItem = { ...item };
        this.resetItemsForm("edit");
      }
    },
    deleteItem() {
      this.dialogDeleteOne = false;
      this.$emit("update:loading", true);
      this.itemToDelete.isWillRemoveSoon = true;
      if (this.deleteItemDispatch) {
        this.$store.dispatch(this.deleteItemDispatch, {
          id: this.itemToDelete.id,
          params: {
            ...this.staticDispatchParams
          },
          onSuccess: () => {
            this.selected = this.selected.filter(
              i => i.id !== this.itemToDelete.id
            );
            this.$emit("updated");
            this.setItemToDelete();
          },
          onError: () => {
            this.itemToDelete.isWillRemoveSoon = false;
            this.setItemToDelete();
          }
        });
      } else {
        this.deleteItemCallback(this.itemToDelete.id);
        this.selected = this.selected.filter(
          i => i.id !== this.itemToDelete.id
        );
        this.$emit("updated");
        this.setItemToDelete();
      }
    },
    createItem(item) {
      if (this.checkForm({ clear: true })) {
        this.$emit("update:loading", true);
        let itemForSave = {
          ...item,
          ...this.staticDispatchParams
        };
        delete itemForSave.isNewItem;

        if (this.createItemDispatch) {
          this.$store
            .dispatch(this.createItemDispatch, {
              item: itemForSave,
              onSuccess: () => {
                this.customSort(null, ["created_at"], [true]);
                this.$emit("updated");
              },
              errorHandler: this.errorHandler.bind(this, () => {
                this.checkForm();
              })
            })
            .finally(() => {
              this.$emit("update:loading", false);
            });
        } else {
          this.createItemCallback(itemForSave);
          this.customSort(null, ["created_at"], [true]);
          this.$emit("updated");
        }
      }
    },
    edit() {
      this.$emit("update:loading", true);
      if (this.updateItemDispatch) {
        this.$store
          .dispatch(`${this.updateItemDispatch}`, {
            item: {
              ...this.editItem,
              ...this.staticDispatchParams
            },
            onSuccess: () => {
              this.customSort(null, ["created_at"], [true]);
              this.$emit("updated");
              this.setItemToEdit();
            },
            errorHandler: this.errorHandler.bind(this, () => {
              this.checkForm({ mode: "edit", clear: false });
            })
          })
          .finally(() => {
            this.$emit("update:loading", false);
          });
      }
      if (this.checkForm({ mode: "edit", clear: true })) {
        // might need fix later for validation edit form
      } else {
        this.updateItemCallback();
        this.customSort(null, ["created_at"], [true]);
        this.$emit("updated");
        this.setItemToEdit();
      }
    },
    checkForm(option = { mode: undefined, clear: false }) {
      if (option.clear) {
        this.clearBackEndErrors();
      }
      let isValid = true;
      let getInputFromSlot = this.getInputFromSlot.bind(this);
      for (let slot of this.slots) {
        if (slot.form) {
          let form = getInputFromSlot(slot, option.mode);
          if (form && form.validate) {
            isValid = form.validate() && isValid;
          }
        }
      }
      return isValid;
    },
    resetItemsForm(mode) {
      let getInputFromSlot = this.getInputFromSlot.bind(this);
      for (let slot of this.slots) {
        if (slot.form) {
          let form = getInputFromSlot(slot, mode);
          form && form.resetValidation();
        }
      }
    },

    getInputFromSlot(slot, mode = "") {
      let form = this.$refs[slot.name + mode];
      return form && form[0];
    },

    isEditItem(item) {
      return !!item && !!item.id && item.id === this.editItem.id;
    },
    setRef(slot, edit) {
      return slot.name + (edit ? "edit" : "");
    },
    canGiveForm(item, slot) {
      let can = false;
      if (slot && slot.form) {
        if (!slot.form.editOnly && !slot.form.newOnly) {
          can = true;
        }
        if (slot.form.editOnly && !item.isNewItem) {
          can = true;
        }
        if (slot.form.newOnly && item.isNewItem) {
          can = true;
        }

        if (can && slot.form.visibilityFunction) {
          can = slot.form.visibilityFunction(item, slot);
        }
      }
      return can;
    },

    updateDate(item, slot, val) {
      let index = this.list.findIndex(i => i.id === item.id);
      item[slot.form.name || slot.name] = val;
      this.$set(this.list, index, { ...item });
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    formStyle() {
      let number = this.headers.findIndex(i => i.value == "action") + 1;
      let parent = `${this.$el &&
        Object.values(this.$el.attributes)
          .map(i => i.name)
          .filter(i => i.startsWith("data-v"))
          .reduce(
            (ac, i) => `[${i}]${ac}`,
            ""
          )} .v-data-table__mobile-row:nth-child(${number})`;
      if (number) {
        this.style = `
            <style>
                  ${parent} {
                  min-height: 0;
                  }
                  ${parent} .v-data-table__mobile-row__header{
                    display: none;
                  }
                  ${parent} .v-data-table__mobile-row__cell{
                    width: 100%!important;
                  }
                   ${parent} .v-data-table__mobile-row__cell .actions-container{
                    width: 100%!important;
                  }
                </style>
            `;
      } else {
        this.style = "";
      }
    },
    onClickAction(action, item) {
      if (item.isWillRemoveSoon) {
        return;
      }
      if (action.onClickMode === "callback") {
        action.callback && action.callback(item);
        return;
      }

      this.$router.push({
        name: action.to,
        params: {
          id: item.id
        }
      });
    }
  },
  mounted() {
    this.formStyle();
  }
};
</script>

<style lang="scss" scoped>
.container {
  height: calc(100vh - 250px);
  flex: 1 1 100% !important;
}
.fullScreenMobile.container {
  height: auto;
}
.get-lower {
  margin-top: 25px;
}
.data-table {
  height: 100%;
  overflow: hidden;
  &::v-deep {
    td {
      padding-top: 3px;
      padding-bottom: 3px;
    }

    &.theme--light.v-data-table
      .v-data-table-header
      th.sortable
      .v-data-table-header__icon {
      color: rgb(225, 74, 74);
    }
  }
}

.container::v-deep .v-input {
  padding: 0;
  margin: 0;
  .v-input__slot {
    padding: 0;
  }
}
.edit-row-icons {
  cursor: pointer;
  .second {
    transform: translate(-7px, 4px);
  }
}
.container::v-deep {
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type="number"] {
    -moz-appearance: textfield;
  }
  .v-data-table-header {
    padding-bottom: 10px;
    padding-top: 10px;
    div.active .v-data-table-header__icon {
      opacity: 1;
    }
  }
}
.list-item {
  display: flex;
  flex-direction: column;
}
.v-data-table__mobile-row__cell .actions-container {
  .mobile {
    display: inline-block;
  }
  .desktop {
    display: none;
  }
}
.actions-container {
  display: inline-block;
  &:empty {
    display: none;
  }
  i.v-icon:last-child {
    margin-right: 0 !important;
  }
  .mobile {
    display: none;
  }
  .desktop {
    display: inline-flex;
  }
}
.fullScreenMobile::v-deep .v-data-table__mobile-row {
  height: auto;
  .v-data-table__mobile-row__wrapper {
    align-items: flex-start;
    padding-top: 3px;
    padding-bottom: 3px;
  }
}
.fullScreenMobile::v-deep .v-data-table-header-mobile tr {
  height: 56px;
}
.selected {
  background-color: #1976d2;
  color: white;
}
.day-button {
  height: 36px !important;
}
</style>
